import CloudSync from "@mui/icons-material/CloudSync";
import Hiking from "@mui/icons-material/Hiking";
import OpenInNew from "@mui/icons-material/OpenInNew";
import RedoIcon from "@mui/icons-material/Redo";
import Unpublished from "@mui/icons-material/Unpublished";
import Work from "@mui/icons-material/Work";
import { Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { PaletteColor } from "@mui/material/styles";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { MultiLineCell } from "components/MultiLineCell";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CommunicationsQuery,
  CommunicationsQueryVariables,
  CommunicationStatus,
  CommunicationType,
  JobStatus,
} from "generated-graphql/graphql";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useNavigateToFulfillmentReports } from "hooks/useNavigateToFulfillmentReports";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { getJobStatusColor } from "../../Jobs/utils";
import {
  COMMUNICATIONS_QUERY,
  useExemptMailingAddressFromVerification,
  useRequeueCommunication,
  useSyncProofFromLob,
} from "./api";
import { formatAddress, formatCommunicationType, getFacilityName, RecipientType } from "./utils";

type CommunicationRow = CommunicationsQuery["communications"]["items"][number];

const communicationStatusToLabel = (communicationStatus: CommunicationStatus) => {
  switch (communicationStatus) {
    case CommunicationStatus.Error:
      return "Error";
    case CommunicationStatus.Pending:
      return "Pending";
    case CommunicationStatus.SentToEmailClient:
      return "Emailed";
    case CommunicationStatus.SentToMailClient:
      return "Mailed";
    case CommunicationStatus.Draft:
      return "Draft";
    default:
      return prettyPrintEnumValue(communicationStatus);
  }
};

export const CommunicationsTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const navigateToFulfillmentReports = useNavigateToFulfillmentReports({
    openInNewTab: true,
  });

  const {
    selectedId: communicationId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("communicationId");

  const getCommunicationStatusColorCallback = useCallback(
    ({
      communicationStatus,
      jobStatus,
    }: {
      communicationStatus: CommunicationStatus | undefined | null;
      jobStatus: JobStatus | undefined | null;
    }) => {
      switch (communicationStatus) {
        case CommunicationStatus.SentToEmailClient:
        case CommunicationStatus.SentToMailClient:
          return getJobStatusColor({
            jobStatus,
            theme,
          });
        case CommunicationStatus.Error:
          return theme.palette.error;
        case CommunicationStatus.Draft:
        case CommunicationStatus.Pending:
        default:
          return theme.palette.info;
      }
    },
    [theme]
  );

  const handleViewProof = useCallback(
    async (proofDocumentId: string) => {
      const { data } = await getDocumentDownloadLink({
        variables: { id: proofDocumentId },
      });
      if (data?.getDocumentDownloadLink) {
        window.open(data.getDocumentDownloadLink, "_blank");
      }
    },
    [getDocumentDownloadLink]
  );

  const {
    exemptMailingAddressFromVerification: onClickExemptMailingAddressFromVerification,
    loading,
  } = useExemptMailingAddressFromVerification();

  const { requeue: onClickRequeueCommunication, loading: requeueLoading } =
    useRequeueCommunication();

  const { syncProof: onClickSyncProofFromLob, loading: syncProofLoading } = useSyncProofFromLob();

  const getStatusText = useCallback(
    (
      communicationStatus: CommunicationStatus | undefined,
      jobStatus: JobStatus | undefined | null,
      jobStepError: string | undefined | null,
      lobMailStatus: string | undefined | null
    ): { shortText: string; longText?: string } => {
      switch (communicationStatus) {
        case CommunicationStatus.Error:
          // render the job error message
          if (jobStepError?.toLowerCase().includes("undeliverable")) {
            return {
              shortText: "Error: Undeliverable",
              longText: jobStepError,
            };
          }
          return {
            shortText: `Error: ${jobStepError || "Unknown error"}`,
            longText: jobStepError ?? undefined,
          };
        case CommunicationStatus.Pending:
          return {
            shortText: "Pending",
            longText: undefined,
          };
        case CommunicationStatus.SentToEmailClient:
          if (jobStatus === JobStatus.Failed) {
            return {
              shortText: `Emailed with error: ${jobStepError || "Unknown error"}`,
              longText: jobStepError ?? undefined,
            };
          }
          return {
            shortText: "Emailed",
            longText: undefined,
          };
        case CommunicationStatus.SentToMailClient:
          if (lobMailStatus) {
            return {
              shortText: `Mailed (${prettyPrintEnumValue(lobMailStatus)})`,
              longText: undefined,
            };
          }
          if (jobStatus === JobStatus.Failed) {
            return {
              shortText: `Mailed with error: ${jobStepError || "Unknown error"}`,
              longText: jobStepError ?? undefined,
            };
          }
          return {
            shortText: `Mailed (${prettyPrintEnumValue(jobStatus)})`,
            longText: undefined,
          };
        default:
          return {
            shortText: prettyPrintEnumValue(communicationStatus),
            longText: undefined,
          };
      }
    },
    []
  );

  const columns: OmnisearchGridColDef<CommunicationRow>[] = useMemo(
    () => [
      {
        headerName: "Organization",
        field: "tenant",
        flex: 1,
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant.name;
        },
      },
      {
        headerName: "Facility/Facilities",
        field: "facilities",
        flex: 1.2,
        filterKeyType: "facility",
        valueGetter(_, row) {
          const facilities = row.communicationFacilities.map((cf) => getFacilityName(cf.facility));
          if (facilities.length <= 2) {
            return facilities.slice(0, 2);
          }
          return [facilities[0], facilities[1], `+ ${facilities.length - 2} more`];
        },
        renderCell({ value }: { value?: string[] }) {
          if (!value || value.length === 0) return null;
          if (value.length <= 2) {
            return <MultiLineCell lines={value.slice(0, 2)} />;
          }
          return <MultiLineCell lines={value} />;
        },
      },
      {
        headerName: "Recipient Type",
        field: "recipientType",
        flex: 1,
        enumValues: Object.values(RecipientType),
        filterKeyType: "enumAutocomplete",
        valueGetter(_, row) {
          if (row.lepcId) {
            return RecipientType.LEPC;
          }
          if (row.fireDepartmentId) {
            return RecipientType.FIRE_DEPARTMENT;
          }
          if (row.policeDepartmentId) {
            return RecipientType.POLICE_DEPARTMENT;
          }
          if (row.leadAgencyId) {
            return RecipientType.LEAD_AGENCY;
          }
          return RecipientType.CUSTOM;
        },
        valueFormatter(value) {
          switch (value) {
            case RecipientType.LEPC:
              return "LEPC";
            case RecipientType.FIRE_DEPARTMENT:
              return "Fire Department";
            case RecipientType.POLICE_DEPARTMENT:
              return "Police Department";
            case RecipientType.LEAD_AGENCY:
              return "Lead Agency";
            case RecipientType.CUSTOM:
              return "Custom";
            default:
              return prettyPrintEnumValue(value);
          }
        },
      },
      {
        headerName: "Recipient",
        field: "recipientName",
        flex: 1,
      },
      {
        headerName: "Method",
        field: "communicationType",
        flex: 1,
        enumValues: Object.values(CommunicationType),
        filterKeyType: "enumAutocomplete",
        valueGetter(_, row) {
          return formatCommunicationType(row?.communicationType);
        },
      },
      {
        headerName: "Address",
        field: "address",
        flex: 1.2,
        valueGetter(_, row) {
          if (row.communicationType === CommunicationType.Email) {
            return row.emailRecipientAddresses.join(", ");
          }
          return formatAddress(row);
        },
      },
      {
        headerName: "Document(s)",
        field: "communicationDocuments",
        flex: 1.2,
        valueGetter(_, row) {
          return [...row.communicationDocuments]
            .sort((a, b) => a.sequence - b.sequence)
            .map((cd) => cd.document.title)
            .join(", ");
        },
        renderCell({ value }: { value?: string }) {
          if (!value) return null;
          const documents = value.split(", ");
          if (documents.length <= 2) {
            return (
              <Tooltip title={value}>
                <Typography variant="body2" noWrap>
                  {value}
                </Typography>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={value}>
              <Typography variant="body2" noWrap>
                {`${documents.slice(0, 2).join(", ")} + ${documents.length - 2} more`}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Completed By",
        field: "completedByUser",
        flex: 1,
        valueGetter(_, row) {
          if (row.completedByUser?.email) {
            return row.completedByUser.email;
          }
          const systemUsers = ["workflow-manager", "send-mail", "send-email"];
          if (systemUsers.includes(row.updatedBy)) {
            return "Workflow Manager";
          }
          return row.updatedBy;
        },
      },
      {
        headerName: "Sent At",
        field: "completedAt",
        flex: 1,
        filterKeyType: "date",
        valueGetter(_, row) {
          if (!row.completedAt) return null;
          return prettyPrintDateTime(row.completedAt);
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        enumValues: Object.values(CommunicationStatus),
        filterKeyType: "enumAutocomplete",
        enumPresentationFunction: (enumValue) =>
          communicationStatusToLabel(enumValue as CommunicationStatus),
        renderCell({ row, value }: { row: CommunicationRow; value?: CommunicationStatus }) {
          const jobStatus = row.job?.status;
          const jobStepError = row.job?.stepError;
          const color: PaletteColor = getCommunicationStatusColorCallback({
            communicationStatus: value,
            jobStatus,
          });
          const { shortText, longText } = getStatusText(
            value,
            jobStatus,
            jobStepError,
            row.lobMail?.status
          );
          return (
            <Tooltip
              title={longText}
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "1rem",
                    maxWidth: "500px",
                  },
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: color.main,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shortText}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1,
        filterKeyType: "date",
        filterable: false,
        valueGetter(_, row) {
          if (!row.createdAt) return null;
          return prettyPrintDateTime(row.createdAt);
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        flex: 0.7,
        sortable: false,
        filterable: false,
        type: "actions",
        getActions: (params) => {
          const actions = [
            <GridActionsCellItem
              key="view-proof"
              icon={<OpenInNew />}
              label="View Proof"
              disabled={!params.row.proofDocument}
              showInMenu
              onClick={(e) => {
                e.stopPropagation();
                if (!params.row.proofDocument) return;
                handleViewProof(params.row.proofDocument.id);
              }}
            />,
            <GridActionsCellItem
              key="sync-proof"
              icon={<CloudSync />}
              label="Sync Proof"
              disabled={
                !!params.row.proofDocument ||
                syncProofLoading ||
                params.row.communicationType === CommunicationType.Email ||
                !params.row.lobMail
              }
              showInMenu
              onClick={(e) => {
                e.stopPropagation();
                onClickSyncProofFromLob(params.row.id);
              }}
            />,
            <GridActionsCellItem
              key="go-to-job"
              icon={<Work />}
              label="Go to Job"
              disabled={!params.row.job}
              showInMenu
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/staff/jobs/${params.row.job?.id}`, "_blank");
              }}
            />,
            <GridActionsCellItem
              key="go-to-report"
              icon={<Hiking />}
              label="Go to Report"
              disabled={!(params.row.tierIIReports?.length ?? 0)}
              showInMenu
              onClick={(e) => {
                e.stopPropagation();
                if (params.row.tierIIReports?.length) {
                  navigateToFulfillmentReports(params.row.tierIIReports.map((r) => r.id));
                }
              }}
            />,
            (params.row.submissions?.length ?? 0) > 0 ? (
              <GridActionsCellItem
                key="go-to-submission"
                icon={<OpenInNew />}
                label={
                  (params.row.submissions?.length ?? 0) === 1
                    ? "View submission"
                    : "View submissions"
                }
                showInMenu
                onClick={(e) => {
                  e.stopPropagation();
                  const submissions = params.row.submissions ?? [];
                  if (submissions.length === 1) {
                    navigate(`/staff/fulfillment/waste/submissions/${submissions[0].id}`);
                  } else if (submissions.length > 1) {
                    const ids = submissions.map((s) => s.id);
                    const wasteGridSearch = `submissionId:${ids.join("⁋")}`;
                    const url = `/staff/fulfillment/waste?omnisearch=${encodeURIComponent(wasteGridSearch)}`;
                    window.open(url, "_blank");
                  } else {
                    window.open("/staff/fulfillment/waste", "_blank");
                  }
                }}
              />
            ) : null,

            <GridActionsCellItem
              key="exempt-mailing-address"
              icon={<Unpublished />}
              label="Exempt Mailing Address"
              showInMenu
              disabled={
                params.row.status !== CommunicationStatus.Error ||
                loading ||
                (params.row.communicationType !== CommunicationType.CertifiedMail &&
                  params.row.communicationType !== CommunicationType.FirstClassMail)
              }
              onClick={(e) => {
                e.stopPropagation();
                onClickExemptMailingAddressFromVerification(params.row);
              }}
            />,
            <GridActionsCellItem
              key="resend"
              icon={<RedoIcon />}
              label="Resend"
              showInMenu
              disabled={
                requeueLoading ||
                params.row.status === CommunicationStatus.Draft ||
                !!params.row.proofDocument
              }
              onClick={(e) => {
                e.stopPropagation();
                onClickRequeueCommunication(params.row);
              }}
            />,
          ];

          return actions;
        },
      },
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [
      getCommunicationStatusColorCallback,
      getStatusText,
      loading,
      requeueLoading,
      syncProofLoading,
      handleViewProof,
      navigate,
      onClickExemptMailingAddressFromVerification,
      onClickRequeueCommunication,
      onClickSyncProofFromLob,
    ]
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<CommunicationsQuery, CommunicationsQueryVariables, CommunicationRow>
        persistenceKey="communications"
        isURLDriven
        rowHeight="auto"
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        onRowClick={(params) => {
          pushIdToUrl(params.row.id);
        }}
        getRowLink={(row) => `./${row.id}`}
        initialState={{
          columns: {
            columnVisibilityModel: {
              createdAt: false,
            },
          },
        }}
        columns={columns}
        dataQuery={COMMUNICATIONS_QUERY}
        getItems={(data) => data.communications.items}
        getCount={(data) => data.communications.count}
        additionalFilterColumns={[
          {
            key: "customerFacilityId",
            header: "Customer Facility Id",
          },
          {
            key: "trackingId",
            header: "Tracking Id",
          },
        ]}
      />
    </Stack>
  );
};
